<template>
  <Modal
    v-model:open="open"
    max-width="1020px"
    width="90vw"
    data-testid="shipment-form-modal"
    :title="currentStepName"
    @update:open="close"
    @closed="close"
  >
    <!-- Shipment Creation Form -->
    <template v-if="validationSchema">
      <form
        v-show="!shipmentCreated"
        id="shipment-form"
        :key="formKey"
        data-testid="shipment-form"
        @submit.prevent="onSubmit"
      >
        <FormStep>
          <ReceiverSender
            :mode="mode === 'return' ? 'sender' : 'receiver'"
            :values="values"
            @update-city="setReceiverSenderCity"
            @emit-contact="setReceiverSender"
            @update-address="(val) => setReceiverSender(val, true)"
            @update-validation="handleUpdateValidation"
          />
        </FormStep>
        <FormStep>
          <div class="w-full overflow-hidden">
            <Dimensions />
          </div>
        </FormStep>
        <FormStep>
          <OrderLines :is-cross-border="isCrossBorder" />
        </FormStep>
        <FormStep>
          <DangerousGoods />
        </FormStep>
        <FormStep>
          <Carrier
            :values="values"
            :mode="mode"
            @skip-insurance="handleSkipInsurance"
            @goback="changeStep(1)"
            @add-extra-services="addExtraServices"
            @loading="(val: boolean) => (buttonLoading = val)"
          />
        </FormStep>
        <FormStep>
          <ReturnProducts :values="values" />
        </FormStep>
        <FormStep>
          <ExtraServices
            :services="services"
            :values="values"
            :validation-schema="validationSchema"
            :mode="mode"
            @update-validation="handleUpdateServiceValidation"
            @date-update="setNewServiceDate"
            @update-select-value="setNewSelectValue"
          />
        </FormStep>
        <FormStep>
          <InsuranceStep :values="values" />
        </FormStep>
        <FormStep>
          <Confirm
            :values="values"
            :services="services"
            :mode="mode"
            @change-step="changeStep"
            @update-price="(val) => (billingInfo = val)"
          />
        </FormStep>
      </form>
    </template>

    <!-- Loading Overlay -->
    <Overlay v-if="loading" class="absolute inset-0 inset-x-0" />

    <!-- Success Screen -->
    <div v-if="shipmentCreated" class="relative flex h-[70vh] w-full" data-step-key="success">
      <div class="mx-auto my-auto flex min-h-[540px] max-w-[540px] -translate-x-1 transform flex-col justify-center">
        <SuccessBlob />
        <p class="text-heading-title mb-2 w-full text-center">
          {{ t("shipment_is_created") }}
        </p>

        <!-- Package Number and Actions -->
        <div class="mb-2 flex items-center justify-center gap-2">
          <p class="text-body-default text-nowrap">{{ t("package_number") }}</p>
          <Button :variant="ButtonVariant.Tertiary" type="button" @click="copyToClipboard(packageNumber || '')">
            {{ packageNumber }}
            <Icon class="cursor-pointer" src="copy" />
          </Button>
          <Button type="button" :variant="ButtonVariant.Tertiary" @click="navigateToShipments">
            {{ t("open_entity", { entity: t("shipment").toLowerCase() }) }}
            <template #suffix>
              <Icon src="ArrowUpRight" />
            </template>
          </Button>
        </div>

        <p class="text-body-large mb-4 text-center text-pretty">
          {{ t("shipment_is_created_desc") }}
        </p>

        <!-- Label Download/Print Options -->
        <div class="border-bdr-subtle flex w-full gap-2 rounded-xl border px-4 py-0.5">
          <Icon class="mt-[5px]" src="file_text" />
          <p class="text-body-default mt-1 font-medium">Label.pdf</p>
          <div class="ml-auto">
            <a :href="returnLink()" target="_blank" rel="noopener noreferrer">
              <Button class="!inline-block" type="button" :variant="ButtonVariant.Tertiary">
                {{ t("download") }}
              </Button>
            </a>
            <Button class="!inline-block" type="button" :variant="ButtonVariant.Tertiary" @click="openPrinterModal">
              {{ t("print_entity") }}
            </Button>
          </div>
        </div>

        <!-- Success Screen Buttons -->
        <Button class="mt-4 w-full" type="button" @click="close">
          {{ t("close") }}
        </Button>
        <Button class="mt-2 w-full" type="button" :variant="ButtonVariant.Tertiary" @click="createNewShipment">
          {{ t("create_another_shipment") }}
        </Button>
      </div>
    </div>

    <template v-if="validationSchema && !shipmentCreated" #footer>
      <ModalFooter class="px-1.5">
        <Button
          v-if="hasPrevious"
          class="w-full max-w-max"
          type="button"
          :variant="ButtonVariant.Tertiary"
          @click="goToPrev"
          >{{ t("previous") }}</Button
        >
        <Button
          v-if="hasSkip"
          data-testid="skip-step-button"
          class="mr-3 ml-auto w-full max-w-max"
          type="button"
          @click="skip"
          >{{ t("skip_this_step") }}</Button
        >
        <Button
          v-if="hasContinueButton"
          class="w-full max-w-max"
          type="submit"
          :class="{ 'ml-auto': !hasSkip }"
          :disabled="!canSubmit || hasSkip || (!senderReady && currentStepIdx === 0)"
          :variant="isLastStep ? ButtonVariant.Positive : ButtonVariant.Primary"
          :loading="buttonLoading"
          form="shipment-form"
        >
          {{ continueButtonText }}
        </Button>
      </ModalFooter>
    </template>
  </Modal>
  <NoTransferModal v-if="noTransferOpen" ref="noTransferModal" />
</template>

<script setup lang="ts">
  import type { Insurance, IShipment, Product } from "~/types/shipment";

  import {
    createPaymentEventHandler,
    handleSuccessfulShipment as handleSuccess,
    processOrder as processOrderHelper,
    registerShipmentEvents,
    submitShipmentForm,
  } from "../helpers/eventHandlers";
  // Import form and event management helpers
  import {
    createFormUIControls,
    createStepNames,
    goToPrevStep,
    goToLastStep as navigateToLastStep,
    goToStepByKeyName as navigateToStepByKeyName,
    skipInsurance,
    skipStep,
    updateValidation,
  } from "../helpers/formManagement";
  // Import shared state and helpers
  import {
    buildCps,
    isShipmentRedacted,
    loading,
    mode,
    noTransferOpen,
    open,
    return_shipment,
  } from "../helpers/service";
  import {
    prepareShipmentPayload,
    processProducts,
    saveContact,
    setInsuranceValues as setModalInsuranceValues,
    transformReceiverSender,
  } from "../helpers/shipmentOperations";
  // Import components
  import NoTransferModal from "./NoTransferModal.vue";
  import Carrier from "./steps/carrier.vue";
  import Confirm from "./steps/confirm.vue";
  import DangerousGoods from "./steps/dangerousGoods.vue";
  import Dimensions from "./steps/dimensions.vue";
  import ExtraServices from "./steps/extraServices.vue";
  import InsuranceStep from "./steps/insurance.vue";
  import OrderLines from "./steps/orderLines.vue";
  import ReceiverSender from "./steps/receiver_sender.vue";
  import ReturnProducts from "./steps/returnProducts.vue";

  defineOptions({
    name: "NewShipmentModal",
  });

  // Composables
  const { t } = useI18n();
  const yup = useYup();
  const { isDFM, accountIsBusiness } = storeToRefs(useWorkspacesStore());
  const { injectValidationFields } = useValidation();
  const { formatDate } = useDate();
  const router = useRouter();
  const buttonLoading = ref(false);
  // ==========================================
  // STATE & VALIDATION SCHEMA
  // ==========================================
  /**
   * Handles duplicate edit mode
   */
  const editDuplicate = () => {
    changeStep(0);
    if (validationSchema.value && validationSchema.value.length > 0) {
      validationSchema.value[validationSchema.value.length - 1].spec.meta.onPrev = null;
    }
  };

  /**
   * Builds validation schema based on current mode
   */
  const buildValidationSchema = () => {
    // Base schema for dimensions and package info
    const baseSchema = [
      yup
        .object({
          height: yup.number().required(),
          length: yup.number().required(),
          width: yup.number(),
          weight: yup.string().required(),
          has_dangerous_goods: yup.boolean(),
        })
        .meta({ continueButtonText: "continue", key: "dimensions" }),
      yup
        .object({
          order_lines: yup
            .array()
            .of(
              yup.object({
                qty: yup.number().required().min(1),
                item_number: yup.string().required(),
                customs: yup
                  .array()
                  .of(yup.object({ description: yup.string().required() }))
                  .min(1),
              })
            )
            .test("at-least-one-dangerous-goods", t("order_lines.dangerous_goods_required"), function (value) {
              const parent = this.from?.[0]?.value as { has_dangerous_goods?: boolean } | undefined;
              if (!parent?.has_dangerous_goods) return true;
              return (value as { dangerous_goods_set?: boolean }[])?.some((line) => line.dangerous_goods_set) ?? false;
            })
            .default([]),
        })
        .meta({ continueButtonText: "continue", autoskip: true, key: "order_lines" }),
      yup
        .object({
          order_lines: yup
            .array()
            .of(
              yup.object({
                dangerous_goods: yup.array().when("dangerous_goods_set", {
                  is: true,
                  then: (schema) =>
                    schema.min(1).of(
                      yup.object({
                        packaging_quantity: yup.number().required().min(1),
                        packaging_description: yup.string().required(),
                        un_number: yup.number().required(),
                        proper_shipping_name: yup.string().required(),
                        hazard_class: yup.string().required(),
                        quantity_value: yup.number().required().min(0.01),
                        quantity_unit: yup.string().required(),
                        packing_group: yup.string().when("limited_quantity", {
                          is: true,
                          then: (schema) => schema.required(),
                        }),
                      })
                    ),
                }),
              })
            )
            .default([]),
        })
        .meta({ continueButtonText: "continue", autoskip: true, key: "dangerous_goods" }),
      yup
        .object({
          carrier_product_service: yup.string().required(),
          cpsUid: yup.string().required(),
          carrier_product_title: yup.string().required(),
          carrier_product_price: yup.string().required(),
          carrier_product_currency: yup.string().nullable(),
          droppoint: yup
            .string()
            .when("carrier_product_service", {
              is: (val: string) => val && val.includes("droppoint"),
              then: (schema) => schema.required(),
            })
            .nullable(),
        })
        .meta({ continueButtonText: "continue", key: "carrier_product_service" }),
      yup
        .object({
          set_order_lines: yup.array(),
        })
        .meta({ continueButtonText: "continue", autoskip: true, key: "return_products" }),
      yup
        .object({
          services: yup.object({}),
        })
        .meta({ continueButtonText: "continue", skip: true, autoskip: true, key: "services" }),
      yup
        .object({
          insurance: yup.string(),
          insurance_coverage: yup.string(),
          insurance_price: yup.string(),
          insurance_currency: yup.string().nullable(),
        })
        .meta({ continueButtonText: "continue", autoskip: true, key: "insurance" }),
      yup
        .object({
          accept_terms: yup.boolean().required().isTrue(),
          reference: yup.string().nullable(),
          original_shipment_id: yup.string().nullable(),
        })
        .meta({ onPrev: mode.value === "duplicate" ? editDuplicate : null, key: "summary" }),
    ];

    // Add receiver/sender validation based on mode
    if (mode.value === "return") {
      baseSchema.unshift(
        /**@ts-expect-error - We just want to silence this */
        yup
          .object({
            save_sender: yup.boolean().nullable(),
            sender: yup.object().shape({
              name: yup.string().required().label(t("name")),
              email: yup.string().email().required().label(t("email")),
              phone: yup
                .string()
                .required()
                .test(
                  "has_prefix",
                  t("phone_number_must_include_prefix"),
                  (value) => !!(value && value.startsWith("+"))
                )
                .label(t("phone_number"))
                .default("+45"),
              street1: yup.string().required().label(t("street1")),
              street2: yup.string().nullable().label(t("street2")),
              zip_code: yup.string().required().label(t("zip_code")),
              city: yup.string().required().label(t("city")),
              country: yup.string().required().label(t("country")).default("DK"),
              business: yup.boolean().required().default(isDFM.value),
              attention: yup.string().nullable().label(t("attention_name")),
              state: yup.string().nullable(),
            }),
          })
          .meta({ continueButtonText: "continue", key: "sender" })
      );
    } else {
      baseSchema.unshift(
        /**@ts-expect-error - We just want to silence this */
        yup
          .object({
            save_receiver: yup.boolean().nullable(),
            receiver: yup.object().shape({
              name: yup.string().required().label(t("name")),
              email: yup.string().email().required().label(t("email")),
              phone: yup
                .string()
                .required()
                .test(
                  "has_prefix",
                  t("phone_number_must_include_prefix"),
                  (value) => !!(value && value.startsWith("+"))
                )
                .label(t("phone_number"))
                .default("+45"),
              street1: yup.string().required().label(t("street1")),
              street2: yup.string().nullable().label(t("street2")),
              zip_code: yup.string().required().label(t("zip_code")),
              city: yup.string().required().label(t("city")),
              country: yup.string().required().label(t("country")).default("DK"),
              business: yup.boolean().required().default(isDFM.value),
              company_name: yup.string().nullable().label(t("company_name")),
              state: yup.string().nullable(),
            }),
          })
          .meta({ continueButtonText: "continue", key: "receiver" })
      );
    }

    return baseSchema;
  };

  // Create a ref to store the validation schema
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const validationSchemaRef = ref<Record<string, any>[]>([]);

  // TypeScript doesn't correctly infer the complex nested validation schema structure with meta properties and special Yup methods
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const validationSchema = computed<Record<string, any>[]>(() => validationSchemaRef.value);

  // Initialize the validation schema
  validationSchemaRef.value = buildValidationSchema();

  // Set initial autoskip value for services step
  if (validationSchemaRef.value[5]) {
    validationSchemaRef.value[5].spec.meta.autoskip = true;
  }

  /**
   * Rebuilds validation schema based on current mode
   * Should be called when mode changes to ensure the schema is correct
   */
  const rebuildValidationSchema = () => {
    // Store original meta settings
    const servicesStep = getStepMeta("services").autoskip;
    const skipInsuranceValue = getStepMeta("insurance").autoskip;
    const hasDuplicateOnPrev = mode.value === "duplicate";

    // Base schema elements (the core steps that don't change)
    const baseSchema = [
      yup
        .object({
          height: yup.number().required(),
          length: yup.number().required(),
          width: yup.number(),
          weight: yup.string().required(),
          has_dangerous_goods: yup.boolean(),
        })
        .meta({ continueButtonText: "continue", key: "dimensions" }),
      yup
        .object({
          order_lines: yup
            .array()
            .of(
              yup.object({
                qty: yup.number().required().min(1),
                item_number: yup.string().required(),
                customs: yup
                  .array()
                  .of(yup.object({ description: yup.string().required() }))
                  .min(1),
              })
            )
            .test("at-least-one-dangerous-goods", t("order_lines.dangerous_goods_required"), function (value) {
              const parent = this.from?.[0]?.value as { has_dangerous_goods?: boolean } | undefined;
              if (!parent?.has_dangerous_goods) return true;
              return (value as { dangerous_goods_set?: boolean }[])?.some((line) => line.dangerous_goods_set) ?? false;
            })
            .default([]),
        })
        .meta({ continueButtonText: "continue", autoskip: true, key: "order_lines" }),
      yup
        .object({
          order_lines: yup
            .array()
            .of(
              yup.object({
                dangerous_goods: yup.array().when("dangerous_goods_set", {
                  is: true,
                  then: (schema) =>
                    schema.min(1).of(
                      yup.object({
                        packaging_quantity: yup.number().required().min(1),
                        packaging_description: yup.string().required(),
                        un_number: yup.number().required(),
                        proper_shipping_name: yup.string().required(),
                        hazard_class: yup.string().required(),
                        quantity_value: yup.number().required().min(0.01),
                        quantity_unit: yup.string().required(),
                        packing_group: yup.string().when("limited_quantity", {
                          is: true,
                          then: (schema) => schema.required(),
                        }),
                      })
                    ),
                }),
              })
            )
            .default([]),
        })
        .meta({ continueButtonText: "continue", autoskip: true, key: "dangerous_goods" }),
      yup
        .object({
          carrier_product_service: yup.string().required(),
          cpsUid: yup.string().required(),
          carrier_product_title: yup.string().required(),
          carrier_product_price: yup.string().required(),
          carrier_product_currency: yup.string().nullable(),
          droppoint: yup
            .string()
            .when("carrier_product_service", {
              is: (val: string) => val && val.includes("droppoint"),
              then: (schema) => schema.required(),
            })
            .nullable(),
        })
        .meta({ continueButtonText: "continue", key: "carrier_product_service" }),
      yup
        .object({
          set_order_lines: yup.array(),
        })
        .meta({ continueButtonText: "continue", autoskip: true, key: "return_products" }),
      yup
        .object({
          services: yup.object({}),
        })
        .meta({ continueButtonText: "continue", skip: true, autoskip: servicesStep, key: "services" }),
      yup
        .object({
          insurance: yup.string(),
          insurance_coverage: yup.string(),
          insurance_price: yup.string(),
          insurance_currency: yup.string().nullable(),
        })
        .meta({ continueButtonText: "continue", autoskip: skipInsuranceValue, key: "insurance" }),
      yup
        .object({
          accept_terms: yup.boolean().required().isTrue(),
          reference: yup.string().nullable(),
          original_shipment_id: yup.string().nullable(),
        })
        .meta({ onPrev: hasDuplicateOnPrev ? editDuplicate : null, key: "summary" }),
    ];

    // Add receiver/sender validation based on mode
    if (mode.value === "return") {
      baseSchema.unshift(
        /**@ts-expect-error - We just want to silence this */
        yup
          .object({
            save_sender: yup.boolean().nullable(),
            sender: yup.object().shape({
              name: yup.string().required().label(t("name")),
              email: yup.string().email().required().label(t("email")),
              phone: yup
                .string()
                .required()
                .test(
                  "has_prefix",
                  t("phone_number_must_include_prefix"),
                  (value) => !!(value && value.startsWith("+"))
                )
                .label(t("phone_number"))
                .default("+45"),
              street1: yup.string().required().label(t("street1")),
              street2: yup.string().nullable().label(t("street2")),
              zip_code: yup.string().required().label(t("zip_code")),
              city: yup.string().required().label(t("city")),
              country: yup.string().required().label(t("country")).default("DK"),
              business: yup.boolean().required().default(isDFM.value),
              attention: yup.string().nullable().label(t("attention_name")),
            }),
          })
          .meta({ continueButtonText: "continue", key: "sender" })
      );
    } else {
      baseSchema.unshift(
        /**@ts-expect-error - We just want to silence this */
        yup
          .object({
            save_receiver: yup.boolean().nullable(),
            receiver: yup.object().shape({
              name: yup.string().required().label(t("name")),
              email: yup.string().email().required().label(t("email")),
              phone: yup
                .string()
                .required()
                .test(
                  "has_prefix",
                  t("phone_number_must_include_prefix"),
                  (value) => !!(value && value.startsWith("+"))
                )
                .label(t("phone_number"))
                .default("+45"),
              street1: yup.string().required().label(t("street1")),
              street2: yup.string().nullable().label(t("street2")),
              zip_code: yup.string().required().label(t("zip_code")),
              city: yup.string().required().label(t("city")),
              country: yup.string().required().label(t("country")).default("DK"),
              business: yup.boolean().required().default(isDFM.value),
              company_name: yup.string().nullable().label(t("company_name")),
            }),
          })
          .meta({ continueButtonText: "continue", key: "receiver" })
      );
    }

    // Update the validation schema by updating the ref that backs the computed property
    validationSchemaRef.value = baseSchema;
  };

  // Watch mode changes to update schema
  watch(
    () => mode.value,
    () => {
      // Reset form and rebuild validation schema
      rebuildValidationSchema();
      // Reset steps
      currentStepIdx.value = 0;
      stepCounter.value = 0;
    }
  );

  // Form step management
  const currentStepIdx = ref(0);
  const stepCounter = ref(0);

  // Inject necessary step context for child components
  provide("STEP_COUNTER", stepCounter);
  provide("CURRENT_STEP_INDEX", currentStepIdx);

  // Extract the current schema from validation schema
  const currentSchema = computed(() => {
    if (!validationSchema.value) {
      return {};
    }
    return validationSchema.value[currentStepIdx.value];
  });

  provide("CURRENT_STEP_SCHEMA", currentSchema);

  // Form handling with vee-validate
  const initialValues = computed(() => {
    if (!validationSchema.value) {
      return {};
    }
    const entries = validationSchema.value.map((schema) => {
      const acc: Record<string, unknown> = {};
      Object.entries(schema.fields).forEach((field) => {
        const [key, value] = field;
        const typedValue = value as {
          fields?: Record<string, { spec: { default: unknown } }>;
          spec?: { default: unknown };
        };

        if (typedValue.fields) {
          acc[key] = {} as Record<string, unknown>;
          Object.entries(typedValue.fields).forEach((subField) => {
            const [subKey, subValue] = subField;
            (acc[key] as Record<string, unknown>)[subKey] = subValue.spec.default;
          });
        } else if (typedValue.spec) {
          acc[key] = typedValue.spec.default;
        }
      });
      return acc;
    });

    return entries.reduce((acc, entry) => {
      return { ...acc, ...entry };
    }, {});
  });

  const { values, handleSubmit, setValues, setFieldValue, resetForm, setErrors } = useForm({
    validationSchema: currentSchema,
    keepValuesOnUnmount: true,
    initialValues: initialValues.value,
  });

  provide("FORM_VALUES", values);

  // Form validation state
  const isValid = useIsFormValid();
  const isDirty = useIsFormDirty();
  // True once setSender()'s async fetchCustomer() has resolved; gates step-0 submission
  const senderReady = ref(false);

  // Computed states for form UI
  const { isLastStep, hasPrevious, hasSkip, hasContinueButton, continueButtonText, canSubmit } = createFormUIControls(
    currentStepIdx,
    stepCounter,
    currentSchema,
    isValid,
    isDirty,
    values,
    t
  );

  // Dynamic step names based on mode
  const stepNames = createStepNames(t);
  const currentStepName = computed(() => {
    const currentSchemaMetaKey = currentSchema.value?.spec?.meta?.key;
    if (!currentSchemaMetaKey) {
      return null;
    }
    return stepNames.value.find((s) => s.key === currentSchemaMetaKey)?.label || null;
  });

  // Shipment creation state
  const billingInfo = ref<unknown>(null);
  const packageNumber = ref<string | null>(null);
  const packageId = ref<string | null>(null);
  const services = ref<unknown[]>([]);
  const shipmentCreated = ref(false);
  const formKey = ref(0); // Used to force re-render of the form
  const isCrossBorder = ref(true);

  // ==========================================
  // EVENT HANDLERS
  // ==========================================

  onMounted(() => {
    // Register event handlers
    const events = registerShipmentEvents(EventBus, setSender, duplicate);
    events.register();
    // Handle return shipment (if unknown)
    if (return_shipment.value?.order_lines && return_shipment.value?.order_lines.length > 0) {
      setValues({
        order_lines: return_shipment.value.order_lines,
      });
      getStepMeta("order_lines").autoskip = false;
    } else {
      getStepMeta("order_lines").autoskip = true;
    }
    // Make sure the validation schema is correctly initialized for the current mode
    rebuildValidationSchema();

    // Ensure the form is properly initialized with default values for save controls
    if (mode.value === "return") {
      setFieldValue("save_sender", false);
    } else {
      setFieldValue("save_receiver", false);
    }
  });

  onUnmounted(() => {
    // Unregister event handlers
    const events = registerShipmentEvents(EventBus, setSender, duplicate);
    events.unregister();
  });

  // ==========================================
  // METHODS
  // ==========================================

  /**
   * Form submission handler
   */
  const onSubmit = handleSubmit(async (v) => {
    // Save contact if needed
    if (currentStepIdx.value === 0) {
      if (mode.value === "return" && v.save_sender === undefined) {
        setFieldValue("save_sender", false);
      } else if (mode.value !== "return" && v.save_receiver === undefined) {
        setFieldValue("save_receiver", false);
      }
      if (v.save_receiver) {
        await saveContact(v.receiver);
        setFieldValue("save_receiver", false);
      } else if (v.save_sender) {
        await saveContact(v.sender);
        setFieldValue("save_sender", false);
      }
    }
    // If shipment is cross-border, do not skip the first orderlines step.
    // senderReady gates the submit button so sender/receiver are always populated here.
    if (currentStepIdx.value === 0) {
      await homeFetch<boolean>(
        `cross-border?from_country=${values.sender.country}&to_country=${values.receiver.country}`
      ).then((res) => {
        getStepMeta("order_lines").autoskip = !res.data;
        isCrossBorder.value = res.data;
      });
    }

    // if services has "dangerous_goods" service, do not skip the second orderlines
    if (currentStepIdx.value >= 0 && v.has_dangerous_goods) {
      getStepMeta("order_lines").autoskip = false;
      getStepMeta("dangerous_goods").autoskip = false;
      //add dangerous goods to services step so it can be used in summary
    } else if (v.carrier_product_service && (v.carrier_product_service as string).split("_")[0] === "ups") {
      getStepMeta("order_lines").autoskip = false;
    } else if (!isCrossBorder.value) {
      getStepMeta("order_lines").autoskip = true;
    }

    if (!isLastStep.value && validationSchema.value) {
      for (let i = currentStepIdx.value + 1; i < validationSchema.value.length; i++) {
        if (!validationSchema.value[i]?.spec?.meta?.autoskip) {
          currentStepIdx.value = i;
          return;
        }
      }
    }
    if (isLastStep.value) {
      submitForm(v);
    }
  });

  provide("SUBMIT_STEP", onSubmit);

  /**
   * Changes the current step index
   */
  const changeStep = (val: number) => {
    currentStepIdx.value = val;
  };

  /**
   * Navigate to previous step
   */
  function goToPrev() {
    goToPrevStep(currentSchema, currentStepIdx, validationSchema);
  }

  /**
   * Skip current step
   */
  const skip = () => {
    skipStep(validationSchema, currentStepIdx);
  };

  /**
   * Go to the last step
   */
  const goToLastStep = () => {
    navigateToLastStep(currentStepIdx, stepCounter);
  };

  /**
   * Go to a step by key name
   */
  const goToStepByKeyName = (key: string) => {
    navigateToStepByKeyName(key, validationSchema, currentStepIdx);
  };

  /**
   * Closes the modal and resets state
   */
  const close = () => {
    // Reset modal state
    return_shipment.value = null;
    open.value = false;

    // Reset form state
    currentStepIdx.value = 0;
    stepCounter.value = 0;

    // Reset shipment creation state
    shipmentCreated.value = false;
    packageNumber.value = null;
    packageId.value = null;
    billingInfo.value = null;
    services.value = [];

    // Reset mode to "new" - this will trigger the watcher and rebuild the schema
    mode.value = "new";

    // Reset validation schema (reapply default autoskip settings)
    if (validationSchema.value) {
      // Update any steps that need explicit resets
      const servicesStepIndex = validationSchema.value.findIndex((s) => s.spec?.meta?.key === "services");
      if (servicesStepIndex !== -1) {
        getStepMeta("services").autoskip = true;
      }

      // Reset the last step's onPrev for non-duplicate mode
      const lastStepIndex = validationSchema.value.length - 1;
      if (lastStepIndex >= 0 && validationSchema.value[lastStepIndex].spec?.meta) {
        validationSchema.value[lastStepIndex].spec.meta.onPrev = null;
      }

      // Reset insurance skip settings
      skipInsurance(validationSchema, false);
    }

    // Reset loading state
    loading.value = false;

    // Ensure the validation schema is updated for "new" mode
    rebuildValidationSchema();
    resetForm();
  };

  /**
   * Resets the created state to create another shipment
   */
  const createNewShipment = () => {
    shipmentCreated.value = false;

    // Reset mode to new (this will trigger the watcher)
    mode.value = "new";

    // Reset steps
    currentStepIdx.value = 0;
    stepCounter.value = 0;

    // Ensure validation schema is updated for "new" mode
    rebuildValidationSchema();

    // Increment the formKey to force complete re-render of the form and all FormStep components
    formKey.value++;

    // Reset form with the new schema
    resetForm();

    // Re-populate sender from customer account (matches NEWSHIPMENT_OPEN/TOGGLE behavior)
    setSender();
  };

  /**
   * Controls insurance step auto-skipping
   */
  const handleSkipInsurance = (val = false) => {
    skipInsurance(validationSchema, val);
  };

  /**
   * Updates validation schema with extra service fields
   */
  const handleUpdateServiceValidation = (val: { fields: object }) => {
    updateValidation(val, validationSchema, injectValidationFields, values);
  };

  // The Toggle component uses syncVModel:false for named fields, so update:modelValue never fires
  // from child components. Watch form values directly to control the services step skip flag.
  watch(
    () => (values as Record<string, unknown>).services,
    (services) => {
      const servicesStepIdx = validationSchema.value.findIndex((s) => s.spec?.meta?.key === "services");
      if (servicesStepIdx < 0) return;
      const serviceMap = services as Record<string, { isSet?: boolean }>;
      const anySelected = serviceMap && Object.values(serviceMap).some((s) => s?.isSet);
      validationSchema.value[servicesStepIdx].spec.meta.skip = !anySelected;
    },
    { deep: true }
  );

  const handleUpdateValidation = (val: { fields: object; step: number }) => {
    injectValidationFields(val.fields, validationSchema.value, val.step, mode.value === "new" ? "receiver" : "sender");
  };

  /**
   * Sets insurance values on the modal
   */
  const setInsuranceValues = (insurance: Insurance) => {
    setModalInsuranceValues(setValues, insurance, !!accountIsBusiness.value);
  };

  /**
   * Process products after they are fetched
   */
  const onProductsFetched = (products: Product[], shipment: IShipment) => {
    processProducts(setValues, products, shipment, !!accountIsBusiness.value);
  };

  /**
   * Duplicates a shipment for reuse
   */
  const duplicate = (shipment: IShipment, returnShipment?: boolean) => {
    const { products, fetchProducts } = useCps();
    loading.value = true;

    const {
      carrier_name,
      package_number,
      is_focused,
      alt_package_number,
      cancel_at,
      cancellable,
      consignment_id,
      created_at,
      deleted_at,
      id,
      identifier,
      labelless_code,
      latest_tracking_event,
      meta,
      pcn_order_id,
      price,
      price_id,
      rebooked,
      redacted,
      servicepoint,
      shipment_type,
      title,
      updated_at,
      // Destructure as unknown to avoid type errors for properties that might exist at runtime but not in type
      possible_services,
      services_insurance,
      default_insurance,
      order_lines,
      services,
      ...rest
    } = shipment as IShipment & {
      carrier_options?: unknown;
      possible_services?: unknown;
      services_insurance?: unknown;
      default_insurance?: unknown;
    };

    const shipmentRedacted = isShipmentRedacted(shipment);

    let reference = rest.reference || "";

    if (returnShipment) {
      if (reference.length > 0) {
        reference += " - Return";
      }
    }

    resetForm({
      values: {
        ...rest,
        receiver: returnShipment
          ? {
              ...transformReceiverSender(rest.sender),
              business: true,
            }
          : {
              ...transformReceiverSender(rest.receiver),
              business: rest.carrier_product === "business",
            },
        sender: transformReceiverSender(returnShipment ? rest.receiver : rest.sender),
        carrier: returnShipment ? "" : rest.carrier,
        carrier_product: returnShipment ? "" : rest.carrier_product,
        carrier_service: returnShipment ? "" : rest.carrier_service,
        carrier_product_service: returnShipment ? "" : buildCps(rest as IShipment),
        droppoint: returnShipment ? "" : rest.servicepoint_id,
        reference,
        order_lines,
      },
    });

    if (shipmentRedacted) {
      setSender(); // setSender sets senderReady=true on success, shows toast on failure
      resetReceiver();
    } else {
      senderReady.value = true; // sender populated synchronously from shipment data
    }

    if (returnShipment) {
      setValues({
        original_shipment_id: shipment.id,
      });
    }

    if (!returnShipment && services && services.length > 0 && possible_services && possible_services.length > 0) {
      addExtraServices({
        services: possible_services,
        reset: true,
      });

      setValues({
        services: transformExtraServicesForSelection(services),
      });
    }

    useInsurance()
      // The products are re-read in the shipment's own currency so the pre-selected
      // insurance is restored with amounts the customer recognises.
      .fetchInsurances(rest, getCurrencyFromShipment(shipment as IShipment))
      .then((insurances) => {
        if (insurances && insurances.length > 0 && rest.insurance) {
          const selectedInsurance = insurances.find((insurance: Insurance) => insurance.id === rest.insurance?.id);
          if (selectedInsurance) setInsuranceValues(selectedInsurance);
        }
        if (returnShipment && !shipmentRedacted) {
          goToStepByKeyName("carrier_product_service");
          return Promise.resolve();
        } else
          return fetchProducts(
            rest.sender,
            rest.receiver,
            rest.weight,
            rest.height,
            rest.width,
            rest.length,
            order_lines,
            {
              silent: true,
            }
          ).then(({ error, status }) => {
            if (status.value == "error") {
              throw error.value;
            }

            if (products.value && products.value.length > 0) {
              onProductsFetched(products.value, rest);
            }
          });
      })
      .then(() => {
        if (shipmentRedacted) {
          goToStepByKeyName("receiver");
        }
        if (!returnShipment) goToLastStep();
      })
      .catch((error) => {
        console.error(error);
        toastAdd({
          text: `${t("shipment_could_not_be_duplicated")} ${error?.data?.data?.data}`,
        });
        resetForm();
        open.value = false;
      })
      .finally(() => {
        loading.value = false;
      });
  };

  /**
   * Updates the form with sender data
   */
  const setSender = (): Promise<void> => {
    senderReady.value = false;
    return useCustomer()
      .fetchCustomer()
      .then((customerData) => {
        if (customerData.data) {
          const data = customerData.data as Record<string, unknown>;
          const mappedData = {
            city: data.city || "",
            country: data.country_iso || "",
            email: data.email || "",
            name: data.name || "",
            attention: data.attention || "",
            phone: data.phone || "",
            phone_prefix: data.phone_prefix || "",
            street1: data.street1 || "",
            street2: data.street2 || "",
            zip_code: data.zip_code || "",
            business: accountIsBusiness.value,
          };
          if (mode.value === "return") {
            resetForm({
              values: {
                sender: emptyReceiverSender,
                receiver: mappedData,
              },
            });
          } else {
            resetForm({
              values: {
                sender: mappedData,
              },
            });
          }
        }
        senderReady.value = true;
      })
      .catch(() => {
        // senderReady stays false — button remains disabled so the user cannot submit
        // with an unpopulated sender; the toast tells them to retry.
        toastAdd({ text: t("failed_to_load_sender"), error: true });
      });
  };

  /**
   * Resets the receiver/sender fields based on mode
   */
  const resetReceiver = () => {
    if (mode.value === "return") {
      setValues({
        sender: emptyReceiverSender,
      });
    } else {
      setValues({
        receiver: emptyReceiverSender,
      });
    }
  };

  /**
   * Updates select value in services
   */
  const setNewSelectValue = (object: { service: { carrier_key: string }; field: { key: string }; val: unknown }) => {
    setValues({
      services: {
        [object.service.carrier_key]: {
          [object.field.key]: object.val,
        },
      },
    });
  };

  /**
   * Updates service date field
   */
  const setNewServiceDate = (object: { service: { carrier_key: string }; extraField: { key: string }; val: Date }) => {
    setValues({
      services: {
        [object.service.carrier_key]: {
          [object.extraField.key]: formatDate(object.val, { format: "yyyy-MM-dd T" }),
        },
      },
    });
  };

  /**
   * Adds extra services to the form
   */
  const addExtraServices = ({ services: s = [], reset = true }: { services: unknown[]; reset?: boolean }) => {
    if (reset) {
      setFieldValue("services", {});
    }

    if (s.length !== 0) {
      services.value = s;
      getStepMeta("services").autoskip = false;
    } else {
      getStepMeta("services").autoskip = true;
    }
  };

  /**
   * Sets receiver/sender field values
   */
  const setReceiverSender = (contact: { data: Record<string, unknown> }, reverse = false) => {
    let key = mode.value === "return" ? "sender" : "receiver";
    if (reverse) {
      key = mode.value === "return" ? "receiver" : "sender";
    }

    // Set business flag
    contact.data.business = contact.data.business ?? !!contact.data.company_name;

    setValues({
      [key]: {
        name: contact.data.business ? contact.data.company_name : contact.data.name,
        attention: contact.data.business ? (contact.data.attention ?? contact.data.name) : undefined,
        email: contact.data.email,
        phone_prefix: contact.data.phone_prefix,
        phone: contact.data.phone,
        street1: contact.data.street1,
        street2: contact.data.street2,
        state: contact.data.region,
        zip_code: contact.data.zip_code,
        city: contact.data.city,
        country: contact.data.country_code,
        business: contact.data.business,
      },
    });
  };

  /**
   * Updates city field for receiver/sender
   */
  const setReceiverSenderCity = (city: string) => {
    const fieldKey = mode.value === "return" ? "sender" : "receiver";
    setFieldValue(`${fieldKey}.city`, city as never);
  };

  /**
   * Submits the form to create a shipment
   */
  const submitForm = async (formData: unknown) => {
    await submitShipmentForm(
      formData,
      billingInfo.value,
      prepareShipmentPayload,
      formatDate,
      homeFetch,
      handleShipmentSuccess,
      handlePaymentProcess
    );
  };

  /**
   * Handles the successful shipment creation
   */
  const handleShipmentSuccess = (shipment: unknown) => {
    handleSuccess(
      shipment,
      t,
      EventBus,
      currentStepIdx,
      (value: boolean) => {
        shipmentCreated.value = value;
      },
      (number: string | null, id: string | null) => {
        packageNumber.value = number;
        packageId.value = id;
      }
    );
  };

  /**
   * Handles payment redirection or processing
   */
  const handlePaymentProcess = (
    data: {
      type: string;
      payment_url: string;
    },
    orderId: string | number | null
  ) => {
    if (data.type !== "invoice" && data.type !== "balance") {
      window.open(data.payment_url);
      window.addEventListener("message", createPaymentEventHandler(orderId, processShipmentOrder));
    } else {
      processShipmentOrder(orderId);
    }
  };

  /**
   * Processes the order after payment
   */
  const processShipmentOrder = async (shipmentOrderId: string | number | null) => {
    processOrderHelper(shipmentOrderId, homeFetch, handleShipmentSuccess);
  };

  const getStepMeta = (stepKey: string) => {
    if (!validationSchema.value) return null;
    const step = validationSchema.value.find((s) => s.spec?.meta?.key === stepKey);
    return step?.spec.meta || null;
  };

  /**
   * Opens the printer modal
   */
  const openPrinterModal = () => {
    printingAddLabel(packageNumber.value as string);
    printingOpen();
  };

  /**
   * Returns the label download link
   */
  const returnLink = () => {
    return router.resolve({
      name: "shipments-id-label",
      params: { id: packageNumber.value },
    })?.href;
  };

  /**
   * Opens the shipment details in a new tab
   */
  const navigateToShipments = () => {
    const link = router.resolve({
      name: "shipments-id",
      params: { id: packageId.value },
    })?.href;

    window.open(link, "_blank");
  };

  const emptyReceiverSender = {
    name: "",
    email: "",
    phone: "",
    street1: "",
    street2: "",
    zip_code: "",
    city: "",
    country: "DK",
  };

  // Expose methods for parent components
  defineExpose({
    addExtraServices,
    values,
    setValues,
    setFieldValue,
    resetForm,
    goToStepByKeyName,
    goToLastStep,
    setErrors,
  });
</script>
